import { Container, SwitchButton, Typography } from '@cleartrip/ct-design-components'
import React, { useState } from 'react'

import { getSrpSearchData } from '@/redux/slices/srpSlice'
import { useAppSelector } from '@/redux/store/hooks'

import { RAVEN_ATTRIBUTE_NAME, RAVEN_EVENT_NAME } from '@/analytics/raven/ravenConstants'
// eslint-disable-next-line import/order
import { ravenSDKTrigger } from '@/analytics/raven/ravenSDKHelper'

import InfoIcon from '@/assets/svg/infoIcon'
import StudentFareIntlModal from '@/components/home/StudentFareIntlModal'
import { PTC_FARE_STRIP_LABEL, type PtcFareTypeCode } from '@/constants/components/fareType'
import useSrpRevampExperiment from '@/hooks/srp/useSrpRevampExperiment'
import { FareType, FilterData, StudentFareFilterProps } from '@/types/srp'
import { formatCurrency } from '@/utils'
import { getFarePricingAttributes, getFaresInfo } from '@/utils/srp/helper'

const StudentFareFilter = ({
  isRT,
  selectedFilters,
  updateFilterData,
  isIntl = false,
  ptcType = 'student',
}: StudentFareFilterProps) => {
  const [showInfoModal, setShowInfoModal] = useState(false)
  const { isSrpRevamp } = useSrpRevampExperiment()
  const { searchData } = useAppSelector(getSrpSearchData)
  const { fares = {}, subTravelOptions = {}, cards = {}, sortData = {} } = searchData || {}
  const stripLabel = PTC_FARE_STRIP_LABEL[ptcType as PtcFareTypeCode] || PTC_FARE_STRIP_LABEL.student

  const getStartingPtcPrice = () => {
    const startingCard = sortData?.ptc_fare_price_up?.J1?.[0]
    const cardDetails = cards?.J1?.[startingCard] || {}
    const faresObjectList: FareType[] =
      getFaresInfo(cardDetails, subTravelOptions, fares, false, false, ptcType) || []
    const { displayPrice } = getFarePricingAttributes({ fare: faresObjectList?.[0] || {}, showPerAdultPricing: false })
    return displayPrice || 0
  }
  const ptcFarePrice = !isRT ? getStartingPtcPrice() : null

  const handleCloseInfoModal = () => {
    setShowInfoModal(false)
  }
  const handleShowInfoModal = () => {
    setShowInfoModal(true)
  }
  const { onwardLeg: onwardLegFilters, returnLeg: returnLegFilters } = selectedFilters || {}
  const { ptcFare: onwardPtcFare } = onwardLegFilters || {}
  const { ptcFare: returnPtcFare } = returnLegFilters || {}
  const isChecked = isRT ? onwardPtcFare?.checked && returnPtcFare?.checked : onwardPtcFare?.checked
  const handleChange = () => {
    updateFilterData((prevState: FilterData) => {
      const nextChecked = !isChecked
      const onwardFilter = {
        ...prevState.onwardLeg,
        ptcFare: {
          ...prevState.onwardLeg.ptcFare,
          checked: nextChecked,
          type: ptcType,
        },
      }
      const returnFilter = {
        ...prevState.returnLeg,
        ptcFare: {
          ...prevState.returnLeg.ptcFare,
          checked: nextChecked,
          type: ptcType,
        },
      }
      if (isRT) {
        return {
          ...prevState,
          onwardLeg: onwardFilter,
          returnLeg: returnFilter,
        }
      } else {
        return {
          ...prevState,
          onwardLeg: onwardFilter,
        }
      }
    })
    const isStudent = ptcType === 'student'
    ravenSDKTrigger(RAVEN_EVENT_NAME.A_UI_ACTION, {
      action_name: isChecked
        ? isStudent
          ? RAVEN_ATTRIBUTE_NAME.STUDENT_FARE_TOGGLE_OFF
          : 'a_ptc_fare_toggle_off'
        : isStudent
          ? RAVEN_ATTRIBUTE_NAME.STUDENT_FARE_TOGGLE_ON
          : 'a_ptc_fare_toggle_on',
      action_type: 'click',
      a_trip_type: isIntl ? 'international' : 'domestic',
      a_fare_type: ptcType,
    })
  }
  // Revamp RT: no top margin — the coupons bar above already owns the 24px gap.
  const rtMarginClassName = isSrpRevamp ? 'mb-6' : 'mb-6 mt-1'
  const marginClassName = isRT ? rtMarginClassName : 'mt-5'
  const radiusClassName = isSrpRevamp ? 'br-8' : 'br-12'
  return (
    <>
      <Container
        className={`px-3 ${radiusClassName} ${marginClassName}`}
        style={{
          paddingBottom: '6px',
          paddingTop: '6px',
          background: 'linear-gradient(88deg, rgba(15, 107, 255, 0.26) -30.31%, rgba(255, 255, 255, 0.30) 63.44%)',
          ...(isSrpRevamp
            ? { border: '1px solid rgba(26, 26, 26, 0.10)', borderRadius: 8 }
            : null),
        }}
      >
        <Container className='flex flex-between'>
          <Container className='flex flex-middle'>
            <Typography variant='HM3'>{stripLabel}</Typography>
            {ptcFarePrice > 0 && (
              <>
                <Container backgroundColor='#B3B3B3' className='mx-2 br-8' height='16px' width='1px' />
                <Typography color='subheading' variant='B2'>
                  {`Starting from ${formatCurrency(ptcFarePrice)}`}
                </Typography>
              </>
            )}
            {ptcType === 'student' ? (
              <Container className='flex flex-middle c-pointer ml-2' onClick={handleShowInfoModal}>
                <InfoIcon fill='#1A1A1A' height='20' width='20' />
              </Container>
            ) : null}
          </Container>
          <SwitchButton checked={isChecked} size={'small'} onChange={handleChange} />
        </Container>
      </Container>
      {showInfoModal && ptcType === 'student' && (
        <StudentFareIntlModal
          open={showInfoModal}
          onClose={handleCloseInfoModal}
          onConfirm={handleCloseInfoModal}
        />
      )}
    </>
  )
}

export default StudentFareFilter
